import { useState, type CSSProperties, type ReactElement } from 'react';
import { apiFetch } from '@/api/v2Client';
import { ConversionStatusBadge } from './ConversionStatusBadge';

// Phase 5.X-docx-to-pdf-service —
// Inspector sub-section shown when the document is a converted DOCX
// (i.e. ConversionStatus !== null). Provides:
//   - The status badge (Converting / Converted / Failed)
//   - Download-PDF button (canonical converted output)
//   - Download-original-DOCX button (audit / re-upload path)
//   - Retry button (admin-only, only when status === 'failed')
//
// The component is self-contained: it talks to the new
// `/api/v1/documents/:id/content` and `/api/v1/documents/:id/reconvert`
// endpoints directly and surfaces toast-style notifications back to
// the caller via the onMessage callback (the parent handles UI).

export interface ConversionPanelProps {
  readonly documentId: string;
  readonly fileName: string;
  readonly conversionStatus: 'pending' | 'success' | 'failed' | string | null | undefined;
  readonly canEdit: boolean;
  /** Refresh the doc list after a successful retry so the new status shows. */
  readonly onReconverted: () => void;
  readonly onError: (message: string) => void;
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '8px 10px',
  marginBottom: 12,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface, #fafafa)',
  alignItems: 'center',
};

const buttonStyle: CSSProperties = {
  fontSize: 12,
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: 'var(--bg, #fff)',
  cursor: 'pointer',
  color: 'var(--text)',
};

const retryButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: 'var(--accent, #4f46e5)',
  borderColor: 'var(--accent, #4f46e5)',
  color: '#fff',
};

const disabledStyle: CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

function buildDownloadUrl(documentId: string, format?: 'original'): string {
  const base = `/api/v1/documents/${documentId}/content`;
  return format ? `${base}?format=${format}` : base;
}

export function ConversionPanel({
  documentId,
  fileName,
  conversionStatus,
  canEdit,
  onReconverted,
  onError,
}: ConversionPanelProps): ReactElement | null {
  const [retrying, setRetrying] = useState(false);

  // Hide entirely for non-converted docs.
  if (conversionStatus === null || conversionStatus === undefined) return null;

  const handleRetry = async (): Promise<void> => {
    setRetrying(true);
    try {
      // phase-fix-v2client-envelope-sweep — apiFetch unwraps { ok, data }, so
      // this resolves to the payload. This previously declared the envelope
      // and read `response.ok` / `response.data.…`: `ok` was always undefined,
      // so the success branch never ran and `response.data.error` threw a
      // TypeError that the catch below reported as the failure message —
      // i.e. retry ALWAYS looked like it failed, even when it succeeded.
      //
      // The route derives its envelope `ok` from `data.status`
      // (`ok: result.status === 'success'`), so `status` alone is sufficient.
      const result = await apiFetch<{ status: 'success' | 'failed'; error: string | null }>(
        `/api/v1/documents/${documentId}/reconvert`,
        { method: 'POST' },
      );
      if (result.status === 'success') {
        onReconverted();
      } else {
        onError(result.error ?? 'Reconvert failed');
      }
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div data-testid="conversion-panel" style={containerStyle}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>DOCX conversion:</span>
      <ConversionStatusBadge status={conversionStatus} />
      <a
        data-testid="conversion-download-pdf"
        href={buildDownloadUrl(documentId)}
        download={fileName.replace(/\.docx$/i, '.pdf')}
        style={{
          ...buttonStyle,
          textDecoration: 'none',
          ...(conversionStatus !== 'success' ? disabledStyle : {}),
        }}
        // Hide click for non-success states by intercepting and
        // forwarding to onError — anchor's href is still rendered for
        // assistive tech parity.
        onClick={(e) => {
          if (conversionStatus !== 'success') {
            e.preventDefault();
            onError('PDF not available — conversion has not succeeded yet.');
          }
        }}
      >
        Download PDF
      </a>
      <a
        data-testid="conversion-download-docx"
        href={buildDownloadUrl(documentId, 'original')}
        download={fileName}
        style={{ ...buttonStyle, textDecoration: 'none' }}
      >
        Download original DOCX
      </a>
      {conversionStatus === 'failed' && canEdit && (
        <button
          type="button"
          data-testid="conversion-retry"
          onClick={() => {
            void handleRetry();
          }}
          disabled={retrying}
          style={{ ...retryButtonStyle, ...(retrying ? disabledStyle : {}) }}
        >
          {retrying ? 'Retrying…' : 'Retry conversion'}
        </button>
      )}
    </div>
  );
}
